import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AuthProvider,
  Gender,
  GenderPreference,
  TOPIC_EMOJI,
  Topic,
  allowedPreferences,
  resolvePreferredGender,
} from '@anon-chat/shared';
import { AgeRangeSlider, AgeSlider } from '@/components/age-slider';
import { AdSlot } from '@/components/ad-slot';
import { ReportSentNotice } from '@/components/report-sent-notice';
import { Button, Modal, Screen } from '@/components/ui';
import { errorText, useTranslations } from '@/shared/i18n';
import { haptic } from '@/shared/telegram';
import { useChatStore } from '@/store/chat.store';
import { useSessionStore } from '@/store/session.store';

/**
 * Все параметры поиска на одном экране.
 *
 * Раньше это была последовательность из пяти шагов. Она уместна при первом
 * знакомстве, но после каждого завершённого диалога человек возвращался сюда и
 * заново прокликивал то, что уже выбрал. Здесь всё видно сразу, а повторный
 * поиск — одна кнопка.
 */
export function OnboardingScreen(): React.ReactNode {
  const dictionary = useTranslations();
  const { draft, setDraft } = useSessionStore();
  const startSearch = useChatStore((state) => state.startSearch);
  const lastError = useChatStore((state) => state.lastError);
  const partnerLeft = useChatStore((state) => state.partnerLeft);
  const dismissPartnerLeft = useChatStore((state) => state.dismissPartnerLeft);
  const [submitting, setSubmitting] = useState(false);

  const openSavedChats = useChatStore((state) => state.openSavedChats);
  const savedChats = useChatStore((state) => state.savedChats);
  const loadSavedChats = useChatStore((state) => state.loadSavedChats);
  const savedAvailable = useSessionStore(
    (state) => state.user?.authProvider === AuthProvider.TELEGRAM,
  );
  const unreadTotal = savedChats.reduce((sum, saved) => sum + saved.unreadCount, 0);

  // Точка непрочитанных нужна до захода в раздел: иначе о новом сообщении от
  // друга человек узнаёт, только если сам туда заглянет.
  useEffect(() => {
    if (savedAvailable) {
      void loadSavedChats();
    }
  }, [savedAvailable, loadSavedChats]);

  /*
    Для «Общения» выбор собеседника обязателен: экран спрашивает, кого искать, и
    трогать поиск, пока на вопрос не ответили, нельзя. Раньше кнопка оживала
    сразу после выбора своего пола и молча искала любого — человек этого не
    выбирал. Во «Флирте» вопроса нет вовсе: пол собеседника задан правилом темы.
  */
  const ready =
    draft.gender !== null &&
    draft.topic !== null &&
    (draft.topic === Topic.FLIRT || draft.preferredGender !== null);

  const preferences = useMemo(
    () => (draft.topic && draft.gender ? allowedPreferences(draft.topic, draft.gender) : []),
    [draft.topic, draft.gender],
  );

  const search = async () => {
    if (!draft.gender || !draft.topic || submitting) {
      return;
    }
    setSubmitting(true);
    await startSearch({
      topic: draft.topic,
      gender: draft.gender,
      age: draft.age,
      preferredGender: resolvePreferredGender(draft.topic, draft.gender, draft.preferredGender),
      preferredAgeMin: draft.preferredAgeMin,
      preferredAgeMax: draft.preferredAgeMax,
    });
    setSubmitting(false);
  };

  return (
    <Screen className="px-5">
      {/*
        Вход в сохранённые диалоги — иконкой, а не отдельным разделом меню:
        постоянная панель съела бы высоту на каждом экране ради двух пунктов.
        Гостю её не показываем: сохранять ему нечего.
      */}
      {savedAvailable ? (
        <div className="flex justify-end pt-3">
          <button
            type="button"
            aria-label={dictionary.saved.open}
            onClick={() => void openSavedChats()}
            className="relative flex size-9 items-center justify-center rounded-full text-text-muted active:bg-surface"
          >
            <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
              <path
                d="M21 12a8 8 0 01-11.6 7.1L4 20l1-4.4A8 8 0 1121 12z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {unreadTotal > 0 ? (
              <span className="absolute right-1 top-1 size-2.5 rounded-full bg-danger" />
            ) : null}
          </button>
        </div>
      ) : null}

      <div className="flex-1 space-y-6 overflow-y-auto scrollbar-hidden py-6">
        <Section title={dictionary.onboarding.topicTitle}>
          <div className="grid grid-cols-2 gap-2.5">
            <Choice
              emoji={TOPIC_EMOJI[Topic.CHAT]}
              label={dictionary.onboarding.topicChat}
              selected={draft.topic === Topic.CHAT}
              onSelect={() => setDraft({ topic: Topic.CHAT })}
            />
            <Choice
              emoji={TOPIC_EMOJI[Topic.FLIRT]}
              label={dictionary.onboarding.topicFlirt}
              selected={draft.topic === Topic.FLIRT}
              // «Флирт» не оставляет выбора: пол собеседника задан правилом темы.
              onSelect={() => setDraft({ topic: Topic.FLIRT, preferredGender: null })}
            />
          </div>
          <p className="mt-2 text-[12px] text-text-muted">
            {draft.topic === Topic.FLIRT
              ? dictionary.onboarding.topicFlirtHint
              : dictionary.onboarding.topicChatHint}
          </p>
        </Section>

        <Section title={dictionary.onboarding.aboutYou}>
          <div className="grid grid-cols-2 gap-2.5">
            <Choice
              emoji="👨"
              label={dictionary.onboarding.male}
              selected={draft.gender === Gender.MALE}
              onSelect={() => setDraft({ gender: Gender.MALE, preferredGender: null })}
            />
            <Choice
              emoji="👩"
              label={dictionary.onboarding.female}
              selected={draft.gender === Gender.FEMALE}
              onSelect={() => setDraft({ gender: Gender.FEMALE, preferredGender: null })}
            />
          </div>
          <div className="mt-2.5">
            <AgeSlider
              compact
              value={draft.age}
              onChange={(age) => setDraft({ age })}
              label={dictionary.onboarding.ageTitle}
            />
          </div>
        </Section>

        <Section title={dictionary.onboarding.preferenceTitle}>
          {/* Для «Флирта» блок скрыт: домен допускает ровно один вариант. */}
          {draft.topic === Topic.CHAT && preferences.length > 0 ? (
            <div className="mb-2.5 grid grid-cols-3 gap-2">
              {preferences.map((preference) => (
                <Choice
                  key={preference}
                  emoji={PREFERENCE_EMOJI[preference]}
                  label={preferenceLabel(dictionary)[preference]}
                  selected={draft.preferredGender === preference}
                  onSelect={() => setDraft({ preferredGender: preference })}
                />
              ))}
            </div>
          ) : null}

          <AgeRangeSlider
            compact
            label={dictionary.onboarding.partnerAgeTitle}
            min={draft.preferredAgeMin}
            max={draft.preferredAgeMax}
            onChange={(range) =>
              setDraft({ preferredAgeMin: range.min, preferredAgeMax: range.max })
            }
            labelFrom={dictionary.onboarding.ageFrom}
            labelTo={dictionary.onboarding.ageTo}
          />
        </Section>

        <p className="text-center text-[12px] text-text-muted">
          {dictionary.onboarding.minimumAgeNotice}
        </p>
      </div>

      {lastError ? (
        <p className="pb-1 text-center text-[13px] text-danger">{errorText(lastError)}</p>
      ) : null}

      <div className="pb-4 pt-2">
        <Button disabled={!ready || submitting} onClick={() => void search()}>
          {dictionary.onboarding.startSearch}
        </Button>
      </div>

      {/* Рекламное место анкеты — под кнопкой, чтобы не отодвигать её вниз. */}
      <AdSlot name="onboarding" className="px-4 pb-4" />

      {/*
        Диалог завершил собеседник. Без окна человек просто оказывался снова на
        анкете и не понимал, что произошло; а чтобы вернуться к разговору,
        приходилось заново подтверждать уже сделанный выбор.
      */}
      <ReportSentNotice />

      <Modal open={partnerLeft} title={dictionary.chat.partnerLeft} onClose={dismissPartnerLeft}>
        <p className="mb-4 text-center text-[14px] text-text-muted">
          {dictionary.onboarding.searchAgainHint}
        </p>
        <div className="flex flex-col gap-2">
          <Button
            disabled={!ready || submitting}
            onClick={() => {
              dismissPartnerLeft();
              void search();
            }}
          >
            {dictionary.onboarding.searchAgain}
          </Button>
          <Button variant="ghost" onClick={dismissPartnerLeft}>
            {dictionary.onboarding.changeSettings}
          </Button>
        </div>
      </Modal>
    </Screen>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    <section>
      <h2 className="mb-2.5 text-[15px] font-medium text-text-muted">{title}</h2>
      {children}
    </section>
  );
}

/** Плитка выбора: крупные карточки со списком на один экран не помещаются. */
function Choice({
  emoji,
  label,
  selected,
  onSelect,
}: {
  emoji: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
}): React.ReactNode {
  return (
    <button
      type="button"
      onClick={() => {
        haptic.select();
        onSelect();
      }}
      className={clsx(
        'flex min-h-[52px] flex-col items-center justify-center gap-0.5 rounded-2xl border px-2 py-2.5',
        'transition-colors',
        selected
          ? 'border-accent bg-accent/12 text-text'
          : 'border-border bg-surface text-text-muted active:bg-surface-raised',
      )}
    >
      <span className="text-[19px] leading-none">{emoji}</span>
      <span className="text-[13px] font-medium leading-tight">{label}</span>
    </button>
  );
}

const PREFERENCE_EMOJI: Record<GenderPreference, string> = {
  [GenderPreference.MALE]: '👨',
  [GenderPreference.FEMALE]: '👩',
  [GenderPreference.ANY]: '🎲',
};

const preferenceLabel = (
  dictionary: ReturnType<typeof useTranslations>,
): Record<GenderPreference, string> => ({
  [GenderPreference.MALE]: dictionary.onboarding.preferenceMale,
  [GenderPreference.FEMALE]: dictionary.onboarding.preferenceFemale,
  [GenderPreference.ANY]: dictionary.onboarding.preferenceAny,
});
